import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { ExportInfoDto } from './../../../../data-transfer/entities/export-info-dto';
import { Component, EventEmitter, Output, input, computed, inject, signal, OnInit, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { ExportInfoPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { PaginationResponseDto } from 'src/app/data-transfer/entities/pagination-response-dto';

@Component({
  selector: 'app-export-list-table',
  templateUrl: './export-list-table.component.html',
  styleUrls: ['./export-list-table.component.scss'],
  animations: [],
})
export class ExportListTableComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly displayedColumns = input<string[]>();
  readonly dataSource = input.required<PaginationDataSource<any>>();
  readonly pageSizeOptions = input<number[]>([10, 25, 50, 100]);
  readonly pageSize = input<number>(10);
  readonly organizations = input<OrganizationDto[]>([]);

  @Output() changeTrackingEvent = new EventEmitter<ExportInfoDto<any, any>>();
  @Output() deleteImportEvent = new EventEmitter<ExportInfoDto<any, any>>();

  private readonly currentPage = signal<PaginationResponseDto<any> | undefined>(undefined);
  private readonly filteredColumnsSignal = signal<string[]>([]);
  readonly filteredColumns = this.filteredColumnsSignal.asReadonly();

  private readonly isLoadingSignal = signal(true);
  readonly isLoading = this.isLoadingSignal.asReadonly();

  ngOnInit(): void {
    const dataSource = this.dataSource();
    dataSource.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(page => {
      this.currentPage.set(page);
    });
    dataSource.filteredColumns.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(filteredColumns => {
      this.filteredColumnsSignal.set(filteredColumns);
    });
    dataSource.isLoading.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(loading => {
      this.isLoadingSignal.set(loading);
    });
  }

  readonly totalItemCount = computed(() => this.currentPage()?.totalItemCount ?? 0);
  readonly currentPageIndex = computed(() => this.currentPage()?.pageIndex ?? 0);

  private readonly pageSizeOverride = signal<number | undefined>(undefined);
  readonly currentPageSize = computed(() => this.pageSizeOverride() ?? this.pageSize());

  // Table data source that includes placeholder rows when loading.
  readonly tableDataSource = computed(() => {
    if (this.isLoading()) {
      return Array.from({ length: this.currentPageSize() }, () => ({}));
    }
    return this.dataSource();
  });

  protected readonly paginationFields = ExportInfoPaginationFields;

  readonly organizationOptions = computed(() => {
    return this.organizations()
      .map(organization => ({ name: organization.name, value: organization.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  onPageChanged(event: PageEvent) {
    this.pageSizeOverride.set(event.pageSize);
    this.dataSource().fetchData(event.pageIndex, event.pageSize);
  }

  onSortChanged(event: Sort) {
    const fieldName = this.getPaginationFieldName(event.active);
    if (!fieldName) {
      return;
    }

    if (event.direction) {
      this.dataSource().sort(fieldName, event.direction === 'asc');
    } else {
      this.dataSource().removeSorting();
    }
  }

  changeTrackingClicked(element: ExportInfoDto<any, any>) {
    this.changeTrackingEvent.emit(element);
  }

  deleteImportClicked(element: ExportInfoDto<any, any>) {
    this.deleteImportEvent.emit(element);
  }

  private getPaginationFieldName(column: string): string | undefined {
    switch (column) {
      case 'importCandidateId': return ExportInfoPaginationFields.ImportCandidateId;
      case 'exportTimestamp': return ExportInfoPaginationFields.ExportTimestamp;
      case 'exportProfile': return ExportInfoPaginationFields.ExportProfile;
      case 'status': return ExportInfoPaginationFields.Status;
      case 'tracking': return ExportInfoPaginationFields.Tracking;
      case 'packagingId': return ExportInfoPaginationFields.Id;
      case 'productName': return ExportInfoPaginationFields.ProductName;
      case 'articleName': return ExportInfoPaginationFields.ArticleName;
      case 'version': return ExportInfoPaginationFields.Version;
      case 'organization': return ExportInfoPaginationFields.OrganizationId;
      default: return undefined;
    }
  }

}
